import { Injectable } from '@angular/core';
import { TelemetryService } from './telemetry.service';

/**
 * Login Performance Metrics Service
 * 
 * Lightweight service to track token login flow duration from ThirdPartyLoginGuard to SessionComponent.
 * Sends simple duration metric to FullStory for p50/p90/p99 monitoring.
 */

@Injectable({
  providedIn: 'root',
})
export class LoginPerformanceMetricsService {
  private startTime: number | null = null;

  constructor(private telemetry: TelemetryService) {}

  /**
   * Check if currently tracking token login
   */
  isTracking(): boolean {
    return this.startTime !== null;
  }

  /**
   * Start tracking token login flow
   */
  startTracking(): void {
    if (this.isTracking()) {
      // Already tracking, don't start again
      return;
    }

    this.startTime = Date.now();
  }

  /**
   * Complete tracking and send metrics to FullStory
   * Safe to call even if tracking was never started (no-op)
   */
  completeAndSend(): void {
    if (!this.isTracking()) {
      // Not tracking, nothing to complete
      return;
    }

    const totalTimeMs = Date.now() - this.startTime!;
    const totalTimeSeconds = Math.round(totalTimeMs / 1000);

    // Send simple duration metric to FullStory (in seconds, rounded)
    this.telemetry.logBasicEvent('[token_login_performance]', {
      total_time_seconds: totalTimeSeconds,
    });

    this.reset();
  }

  /**
   * Reset tracking state
   */
  private reset(): void {
    this.startTime = null;
  }
}
